import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import StatusBadge from "./Badge/StatusBadge";
import StatusTagSelect from "./StatusTagSelect/StatusTagSelect";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../context/AuthContext";
import { pl } from "date-fns/locale";
registerLocale("pl", pl);

const DataTableApp = ({
  id = "dataTableApp",
  data,
  itemsPerPage,
  columnHeaders,
  hiddenColumns = [],
  initialFilterStatuses = [],
  copyHandler,
  deleteHandler,
  colorMode = "Client",
  lockStatuses = false,
  showCaseMessageIndicator = false,
}) => {
  const { userId } = useAuth();

  const getSavedFilters = () => {
    const savedFilters = localStorage.getItem(`filters_${userId}_${id}`);
    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);
      return {
        ...parsed,
        filterDateFrom: parsed.filterDateFrom
          ? new Date(parsed.filterDateFrom)
          : null,
        filterDateTo: parsed.filterDateTo
          ? new Date(parsed.filterDateTo)
          : null,
        filterLastUpdateFrom: parsed.filterLastUpdateFrom
          ? new Date(parsed.filterLastUpdateFrom)
          : null,
        filterLastUpdateTo: parsed.filterLastUpdateTo
          ? new Date(parsed.filterLastUpdateTo)
          : null,
      };
    }
    return {
      filterNIP: "",
      filterExporterName: "",
      filterNumber: "",
      filterStatuses: [],
      filterOperatorName: "",
      filterDateFrom: null,
      filterDateTo: null,
      filterLastUpdateFrom: null,
      filterLastUpdateTo: null,
    };
  };

  const savedFilters = getSavedFilters();

  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("Aktualizacja");
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortedData, setSortedData] = useState([]);
  const [filterNIP, setFilterNIP] = useState(savedFilters.filterNIP);
  const [filterExporterName, setFilterExporterName] = useState(
    savedFilters.filterExporterName,
  );
  const [filterNumber, setFilterNumber] = useState(savedFilters.filterNumber);
  const [filterStatuses, setFilterStatuses] = useState(
    savedFilters.filterStatuses,
  );
  const [filterOperatorName, setFilterOperatorName] = useState(
    savedFilters.filterOperatorName,
  );
  const [filterDateFrom, setFilterDateFrom] = useState(
    savedFilters.filterDateFrom,
  );
  const [filterDateTo, setFilterDateTo] = useState(savedFilters.filterDateTo);
  const [filterLastUpdateFrom, setFilterLastUpdateFrom] = useState(
    savedFilters.filterLastUpdateFrom,
  );
  const [filterLastUpdateTo, setFilterLastUpdateTo] = useState(
    savedFilters.filterLastUpdateTo,
  );
  const [showFilters, setShowFilters] = useState(false);
  let navigate = useNavigate();

  function editApplication(
    businessId,
    step = 1,
    view = null,
    focus = null
  ) {
    let url = "/application/edit/" + step + "/" + businessId;
    const params = new URLSearchParams();
    if (view) params.set("view", view);
    if (focus) params.set("focus", focus);
    const search = params.toString();
    if (search) {
      url += "?" + search;
    }
    navigate(url, { replace: true });
  }

  useEffect(() => {
    const filtersToSave = {
      filterNIP,
      filterExporterName,
      filterNumber,
      filterStatuses,
      filterOperatorName,
      filterDateFrom: filterDateFrom ? filterDateFrom.toISOString() : null,
      filterDateTo: filterDateTo ? filterDateTo.toISOString() : null,
      filterLastUpdateFrom: filterLastUpdateFrom
        ? filterLastUpdateFrom.toISOString()
        : null,
      filterLastUpdateTo: filterLastUpdateTo
        ? filterLastUpdateTo.toISOString()
        : null,
    };

    localStorage.setItem(
      `filters_${userId}_${id}`,
      JSON.stringify(filtersToSave),
    );
  }, [
    filterNIP,
    filterExporterName,
    filterNumber,
    filterStatuses,
    filterOperatorName,
    filterDateFrom,
    filterDateTo,
    filterLastUpdateFrom,
    filterLastUpdateTo,
    userId,
    id,
  ]);

  useEffect(() => {
    let filteredData = data;
    if (filterNIP) {
      filteredData = filteredData.filter((item) =>
        item.NIP.includes(filterNIP),
      );
    }
    if (filterNumber) {
      filteredData = filteredData.filter((item) =>
        item.Numer.toString().includes(filterNumber),
      );
    }
    if (filterExporterName) {
      filteredData = filteredData.filter((item) =>
        item.Eksporter.toLowerCase().includes(filterExporterName.toLowerCase()),
      );
    }
    if (filterStatuses.length) {
      filteredData = filteredData.filter((item) =>
        filterStatuses.includes(item.Status),
      );
    }
    if (filterOperatorName) {
      filteredData = filteredData.filter((item) =>
        item["Obsługuje"]
          .toLowerCase()
          .includes(filterOperatorName.toLowerCase()),
      );
    }
    if (filterDateFrom) {
      filteredData = filteredData.filter(
        (item) =>
          new Date(item["Złożono"]) >= filterDateFrom.setHours(0, 0, 0, 0),
      );
    }
    if (filterDateTo) {
      filteredData = filteredData.filter(
        (item) =>
          new Date(item["Złożono"]) <= filterDateTo.setHours(23, 59, 59, 999),
      );
    }
    if (filterLastUpdateFrom) {
      filteredData = filteredData.filter(
        (item) =>
          new Date(item["Aktualizacja"]) >=
          filterLastUpdateFrom.setHours(0, 0, 0, 0),
      );
    }
    if (filterLastUpdateTo) {
      filteredData = filteredData.filter(
        (item) =>
          new Date(item["Aktualizacja"]) <=
          filterLastUpdateTo.setHours(23, 59, 59, 999),
      );
    }
    if (sortColumn) {
      const sorted = [...filteredData].sort((a, b) => {
        if (a[sortColumn] < b[sortColumn]) {
          return sortOrder === "asc" ? -1 : 1;
        }
        if (a[sortColumn] > b[sortColumn]) {
          return sortOrder === "asc" ? 1 : -1;
        }
        return 0;
      });
      setSortedData(sorted);
    } else {
      setSortedData(filteredData);
    }
  }, [
    sortColumn,
    sortOrder,
    data,
    filterNIP,
    filterNumber,
    filterExporterName,
    filterStatuses,
    filterOperatorName,
    filterDateFrom,
    filterDateTo,
    filterLastUpdateFrom,
    filterLastUpdateTo,
  ]);

  useEffect(() => {
    setShowFilters(false);
    if (initialFilterStatuses.length) {
      setFilterStatuses(initialFilterStatuses);
      setShowFilters(true);
    }

    let hasActiveFilters =
      filterNIP ||
      filterExporterName ||
      filterNumber ||
      filterStatuses.length > 0 ||
      filterOperatorName ||
      filterDateFrom ||
      filterDateTo ||
      // filterLastUpdateFrom ||
      filterLastUpdateTo;

    if (!colorMode === "Client" && filterLastUpdateFrom) {
      hasActiveFilters = hasActiveFilters || filterLastUpdateFrom;
    }

    if (hasActiveFilters) {
      setShowFilters(true);
    }
  }, [initialFilterStatuses]);

  useEffect(() => {
    if (colorMode !== "Client") {
      if (!filterLastUpdateFrom) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        setFilterLastUpdateFrom(thirtyDaysAgo);
      }
    }
  }, [colorMode]);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const renderCell = (row, header) => {
    const value = row[header];

    switch (header) {
      case "Eksporter":
        return value;
      case "Obsługuje":
        if (
          value &&
          typeof value === "string" &&
          value !== "Brak przypisania"
        ) {
          const parts = value.split(" ");
          return (
            <div>
              {parts[0]}
              <br />
              {parts.slice(1).join(" ")}
            </div>
          );
        }
        return value;
      case "Data utworzenia":
      case "Złożono":
      case "Aktualizacja":
        if (value && typeof value === "string") {
          const [date, time] = value.split(" ");
          return (
            <div>
              {date}
              <br />
              {time || ""}
            </div>
          );
        }
        return value;
      case "Status":
        return <StatusBadge text={value} colorMode={colorMode} />;
      case "Action":
        return (
          <div className="d-flex flex-row">
            <button
              className="btn btn-outline-primary h2 tooltip"
              onClick={() => editApplication(value)}
            >
              Otwórz
              <span className="tooltiptext">Otwórz / edytuj wniosek</span>
            </button>
            {colorMode !== "Client" &&
              row.FirstApplicantResponseStep !== null &&
              row.FirstApplicantResponseStep !== undefined && (
                <button
                  className="btn tooltip btn-outline-danger p-0 ms-1 me-auto w2 h2 border-danger border-1"
                  onClick={() => {
                    if (row.FirstApplicantResponseStep === 0) {
                      editApplication(value, 1, "metadata");
                    } else {
                      editApplication(
                        value,
                        row.FirstApplicantResponseStep,
                        "application",
                      );
                    }
                  }}
                >
                  <span className="bi bi-envelope fs-3"></span>
                  <span className="tooltiptext">
                    Nowa wiadomość od wnioskodawcy
                  </span>
                </button>
              )}
            {showCaseMessageIndicator && row.HasUnreadCaseMessage && (
              <button
                type="button"
                className="btn tooltip btn-outline-danger p-0 ms-1 me-auto w2 h2 border-danger border-1"
                onClick={() =>
                  editApplication(value, 1, "metadata", "conversation")
                }
                aria-label="Otwórz nieprzeczytaną wiadomość w konwersacji"
              >
                <span
                  className="bi bi-chat-left-text-fill fs-3"
                  aria-hidden="true"
                ></span>
                <span className="tooltiptext text-wrap">
                  Nieprzeczytana wiadomość od wnioskodawcy
                </span>
              </button>
            )}
            {colorMode === "Client" && (
              <button
                className="btn btn-outline-primary ms-1 h2 tooltip"
                onClick={() => copyHandler(value, row["Nazwa"])}
              >
                <span className="bi bi-copy fs-5"></span>
                <span className="tooltiptext">Kopiuj wniosek</span>
              </button>
            )}
            {colorMode === "Client" &&
              row["Status"].toLowerCase() === "roboczy" && (
                <button
                  className="btn btn-outline-primary ms-1 h2 tooltip"
                  onClick={() => deleteHandler(value, userId)}
                >
                  <span className="bi bi-trash fs-5"></span>
                  <span className="tooltiptext">Usuń wniosek</span>
                </button>
              )}
          </div>
        );
      default:
        return value;
    }
  };

  const getCellClassName = (header) => {
    switch (header) {
      case "Eksporter":
        return "cell-exporter";
      case "Obsługuje":
        return "cell-name-break";
      case "Data utworzenia":
      case "Złożono":
      case "Aktualizacja":
        return "cell-datetime";
      default:
        return "";
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);

  const clearAllFilters = () => {
    setFilterNIP("");
    setFilterExporterName("");
    setFilterNumber("");
    if (!lockStatuses) {
      setFilterStatuses([]);
    }
    setFilterOperatorName("");
    setFilterDateFrom(null);
    setFilterDateTo(null);
    setFilterLastUpdateFrom(null);
    setFilterLastUpdateTo(null);
  };

  return (
    <div className="data-table-container">
      {colorMode !== "Client" && (
        <div className="border-top pt-1 mb-1">
          <label htmlFor="filterStatus" className="form-label">
            Status
          </label>
          <StatusTagSelect
            onChange={setFilterStatuses}
            data={filterStatuses}
            locked={lockStatuses}
          />
        </div>
      )}
      {!showFilters ? (
        <div className="d-flex flex-row mb-1">
          <button
            className="btn btn-outline-primary ms-auto w11 h2"
            onClick={() => setShowFilters(true)}
          >
            <i className="bi bi-funnel me-1" />
            Pokaż filtry
          </button>
        </div>
      ) : (
        <div className="d-flex flex-column border-bottom mb-1">
          <div className="d-flex flex-row mb-1">
            <button
              className="btn btn-outline-primary ms-auto w11 h2"
              onClick={() => setShowFilters(false)}
            >
              <i className="bi bi-funnel me-1" />
              Ukryj filtry
            </button>
          </div>
          <div className="d-flex flex-row">
            <div className="d-flex flex-column w13">
              <label htmlFor="filterNIP" className="form-label">
                NIP
              </label>
              <input
                type="text"
                id="filterNIP"
                className="form-control h2"
                placeholder="Wpisz fragment NIP"
                value={filterNIP}
                onChange={(e) => setFilterNIP(e.target.value)}
              />
            </div>
            <div className="d-flex flex-column ms-1 w23">
              <label htmlFor="filterExporterName" className="form-label">
                Nazwa eksportera
              </label>
              <input
                type="text"
                id="filterExporterName"
                className="form-control h2"
                placeholder="Wpisz fragment nazwy eksportera"
                value={filterExporterName}
                onChange={(e) => setFilterExporterName(e.target.value)}
              />
            </div>
            <div className="d-flex flex-row ms-auto">
              <button
                className="btn btn-outline-primary ms-auto w11 h2"
                onClick={clearAllFilters}
              >
                <i className="bi bi-x-circle me-1" />
                Wyczyść filtry
              </button>
            </div>
          </div>
          <div className="d-flex flex-row">
            <div className="d-flex flex-column w13">
              <label htmlFor="filterNumber" className="form-label">
                Numer wniosku
              </label>
              <input
                type="text"
                id="filterNumber"
                className="form-control h2"
                placeholder="Wpisz fragment numeru wniosku"
                value={filterNumber}
                onChange={(e) => setFilterNumber(e.target.value)}
              />
            </div>
          </div>
          <div className="d-flex flex-row">
            <div className="d-flex flex-column w13">
              <div className="d-flex flex-row">
                <label className="form-label">Data złożenia</label>
              </div>
              <div className="d-flex flex-row">
                <DatePicker
                  selected={filterDateFrom}
                  onChange={(date) => setFilterDateFrom(date)}
                  selectsStart
                  startDate={filterDateFrom}
                  endDate={filterDateTo}
                  placeholderText="Od"
                  locale="pl"
                  dateFormat="yyyy-MM-dd"
                  className="form-control rounded-end-0 border-end-0 w5 h2"
                />
                <div className="form-control rounded-0 border-start-0 border-end-0 w3 px-1 h2">
                  <i className="bi bi-arrow-right"></i>
                </div>
                <DatePicker
                  selected={filterDateTo}
                  onChange={(date) => setFilterDateTo(date)}
                  selectsEnd
                  startDate={filterDateFrom}
                  endDate={filterDateTo}
                  placeholderText="Do"
                  locale="pl"
                  dateFormat="yyyy-MM-dd"
                  className="form-control rounded-start-0 border-start-0 w5 h2"
                />
              </div>
            </div>
            {colorMode !== "Client" && (
              <div className="d-flex flex-column ms-1 w23">
                <label htmlFor="filterOperatorName" className="form-label">
                  Przypisany do
                </label>
                <input
                  type="text"
                  id="filterOperatorName"
                  className="form-control h2"
                  placeholder="Wpisz fragment nazwy operatora"
                  value={filterOperatorName}
                  onChange={(e) => setFilterOperatorName(e.target.value)}
                />
              </div>
            )}
          </div>
          <div className="d-flex flex-row">
            <div className="d-flex flex-column w13">
              <div className="d-flex flex-row">
                <label className="form-label">Data aktualizacji</label>
              </div>
              <div className="d-flex flex-row mb-1">
                <DatePicker
                  selected={filterLastUpdateFrom}
                  onChange={(date) => setFilterLastUpdateFrom(date)}
                  selectsStart
                  startDate={filterLastUpdateFrom}
                  endDate={filterLastUpdateTo}
                  placeholderText="Od"
                  locale="pl"
                  dateFormat="yyyy-MM-dd"
                  className="form-control rounded-end-0 border-end-0 w5 h2"
                />
                <div className="form-control rounded-0 border-start-0 border-end-0 w3 px-1 h2">
                  <i className="bi bi-arrow-right"></i>
                </div>
                <DatePicker
                  selected={filterLastUpdateTo}
                  onChange={(date) => setFilterLastUpdateTo(date)}
                  selectsEnd
                  startDate={filterLastUpdateFrom}
                  endDate={filterLastUpdateTo}
                  placeholderText="Do"
                  locale="pl"
                  dateFormat="yyyy-MM-dd"
                  className="form-control rounded-start-0 border-start-0 w5 h2"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <table className="table">
        <thead>
          <tr>
            {columnHeaders.map(
              (header, index) =>
                !hiddenColumns.includes(header) && (
                  <th
                    key={index}
                    data-column={header}
                    onClick={() => handleSort(header)}
                    style={{ cursor: "pointer" }}
                    className={getCellClassName(header)}
                  >
                    {header !== "Action" ? header : ""}
                    {sortColumn === header &&
                      (sortOrder === "asc" ? (
                        <i className="bi bi-arrow-up"></i>
                      ) : (
                        <i className="bi bi-arrow-down"></i>
                      ))}
                  </th>
                ),
            )}
          </tr>
        </thead>
        <tbody>
          {currentItems.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columnHeaders.map(
                (header, colIndex) =>
                  !hiddenColumns.includes(header) && (
                    <td
                      key={colIndex}
                      data-column={header}
                      className={getCellClassName(header)}
                    >
                      {renderCell(row, header)}
                    </td>
                  ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="d-flex flex-row">
        <nav>
          <ul className="pagination">
            {(() => {
              const totalPages = Math.ceil(sortedData.length / itemsPerPage);
              const pageNumbers = [];
              const maxVisible = 5;
              let startPage = Math.max(
                1,
                currentPage - Math.floor(maxVisible / 2),
              );
              let endPage = startPage + maxVisible - 1;
              if (endPage > totalPages) {
                endPage = totalPages;
                startPage = Math.max(1, endPage - maxVisible + 1);
              }

              if (startPage > 1) {
                pageNumbers.push(
                  <li
                    key={1}
                    className={`page-item ${currentPage === 1 ? "active" : ""}`}
                  >
                    <button onClick={() => paginate(1)} className="page-link">
                      1
                    </button>
                  </li>,
                );
                if (startPage > 2) {
                  pageNumbers.push(
                    <li key="start-ellipsis" className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>,
                  );
                }
              }

              for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(
                  <li
                    key={i}
                    className={`page-item ${currentPage === i ? "active" : ""}`}
                  >
                    <button onClick={() => paginate(i)} className="page-link">
                      {i}
                    </button>
                  </li>,
                );
              }

              if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                  pageNumbers.push(
                    <li key="end-ellipsis" className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>,
                  );
                }
                pageNumbers.push(
                  <li
                    key={totalPages}
                    className={`page-item ${
                      currentPage === totalPages ? "active" : ""
                    }`}
                  >
                    <button
                      onClick={() => paginate(totalPages)}
                      className="page-link"
                    >
                      {totalPages}
                    </button>
                  </li>,
                );
              }

              return pageNumbers;
            })()}
          </ul>
        </nav>
        <span className="text-muted ms-auto">
          Wyświetlane obiekty: {indexOfFirstItem + 1}-
          {Math.min(indexOfLastItem, sortedData.length)} z {sortedData.length}
        </span>
      </div>
    </div>
  );
};

export default DataTableApp;
